import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrderPath } from '../../lib/slug';
import { purchaseOrdersApi, poStatusMeta } from './purchaseOrdersApi';
import '../requisitions/requisitionPrint.css';

const date = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function PurchaseOrderPrintPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { properties } = useProperty();

  const [po, setPo] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, us, locs] = await Promise.all([
        purchaseOrdersApi.get(routeRef),
        catalogApi.listUnits().catch(() => []),
        catalogApi.listLocations().catch(() => []),
      ]);
      setPo(p);
      setUnits(us || []);
      setLocations(locs || []);
      const act = await purchaseOrdersApi.listActivity(p.id, { pageSize: 100 }).catch(() => ({ items: [] }));
      setActivity(act?.items || []);
    } catch (err) {
      setError(err?.message || t('Could not load purchase order.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;
  const property = properties.find((p) => p.id === po?.propertyId);

  if (loading) {
    return <div className="ps-scope rq-print__loading"><Spinner size={26} /></div>;
  }
  if (error || !po) {
    return (
      <div className="ps-scope rq-print__loading">
        <p>{error || t('Not found.')}</p>
        <button type="button" className="rq-print__btn" onClick={() => navigate('/purchase-orders')}>{t('Back')}</button>
      </div>
    );
  }

  const statusMeta = poStatusMeta(po.status);
  const lines = po.lines || [];
  const anyDiscount = lines.some((l) => Number(l.discountPercent) > 0);
  const anyTax = lines.some((l) => Number(l.taxPercent) > 0);
  const coding = lines.find((l) => l.glAccountCode || l.costCenter);
  const hasMoney = (m) => m && Number(m.amount) > 0;
  const breakdownRows = [
    { label: 'Subtotal', value: money(po.subtotal), show: hasMoney(po.subtotal) },
    { label: 'Discount', value: `−${money(po.discountTotal)}`, show: hasMoney(po.discountTotal) },
    { label: 'Tax', value: money(po.taxTotal), show: hasMoney(po.taxTotal) },
    { label: 'Freight', value: money(po.freightAmount), show: hasMoney(po.freightAmount) },
    { label: 'Other charges', value: money(po.otherCharges), show: hasMoney(po.otherCharges) },
  ].filter((r) => r.show);
  const colSpan = 4 + (anyDiscount ? 1 : 0) + (anyTax ? 1 : 0);
  const history = [...activity].reverse();
  const steps = po.approvalSteps || [];
  const hasChain = steps.length > 0;
  const decidedSteps = steps.filter((s) => s.decidedByName);

  return (
    <div className="ps-scope rq-print">
      <div className="rq-print__toolbar">
        <button type="button" className="rq-print__btn rq-print__btn--ghost" onClick={() => navigate(purchaseOrderPath(po))}>
          <Icon.ArrowLeft size={15} /> {t('Back')}
        </button>
        <button type="button" className="rq-print__btn" onClick={() => window.print()}>
          <Icon.Printer size={15} /> {t('Print / Save as PDF')}
        </button>
      </div>

      <div className="rq-doc" role="document">
        <header className="rq-doc__head">
          <div className="rq-doc__brand">
            <div className="rq-doc__property">{property?.name || t('Property')}</div>
            <div className="rq-doc__subtitle">{t('Purchase Order')}</div>
          </div>
          <div className="rq-doc__ref">
            <div className="rq-doc__number">{po.number}</div>
            <div className={`rq-doc__status rq-doc__status--${statusMeta.tone}`}>{t(statusMeta.label)}</div>
          </div>
        </header>

        <h1 className="rq-doc__title">{po.title}</h1>

        <section className="rq-doc__meta">
          <div><span>{t('Supplier')}</span><strong>{po.supplierName || '—'}</strong></div>
          <div><span>{t('Currency')}</span><strong>{po.currency}</strong></div>
          <div><span>{t('Expected')}</span><strong>{date(po.expectedDate)}</strong></div>
          <div><span>{t('Deliver to')}</span><strong>{locationName(po.locationId) || '—'}</strong></div>
          <div><span>{t('Raised by')}</span><strong>{po.createdByName || '—'}</strong></div>
          <div><span>{t('Source requisition')}</span><strong>{po.sourceRequisitionNumber || '—'}</strong></div>
        </section>

        <table className="rq-doc__lines">
          <thead>
            <tr>
              <th className="rq-doc__c-num">#</th>
              <th>{t('Description')}</th>
              <th className="rq-doc__c-qty">{t('Qty')}</th>
              <th className="rq-doc__c-unit">{t('Unit')}</th>
              <th className="rq-doc__c-money">{t('Unit price')}</th>
              {anyDiscount && <th className="rq-doc__c-qty">{t('Disc %')}</th>}
              {anyTax && <th className="rq-doc__c-qty">{t('Tax %')}</th>}
              <th className="rq-doc__c-money">{t('Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.lineId || i}>
                <td className="rq-doc__c-num">{i + 1}</td>
                <td>
                  <div className="rq-doc__desc">{l.description}</div>
                  {l.sku && <div className="rq-doc__sku">{l.sku}</div>}
                </td>
                <td className="rq-doc__c-qty">{l.quantity}</td>
                <td className="rq-doc__c-unit">{uomCode(l.uomId) || '—'}</td>
                <td className="rq-doc__c-money">{money(l.unitPrice)}</td>
                {anyDiscount && <td className="rq-doc__c-qty">{Number(l.discountPercent) > 0 ? `${Number(l.discountPercent)}%` : '—'}</td>}
                {anyTax && <td className="rq-doc__c-qty">{Number(l.taxPercent) > 0 ? `${Number(l.taxPercent)}%` : '—'}</td>}
                <td className="rq-doc__c-money">{money(l.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {breakdownRows.map((r) => (
              <tr key={r.label}>
                <td colSpan={colSpan} className="rq-doc__total-label" style={{ fontWeight: 'normal' }}>{t(r.label)}</td>
                <td className="rq-doc__c-money">{r.value}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={colSpan} className="rq-doc__total-label">{t('Order total')}</td>
              <td className="rq-doc__c-money rq-doc__total-value">{money(po.total)}</td>
            </tr>
          </tfoot>
        </table>

        {coding && (
          <section className="rq-doc__meta" style={{ marginTop: 'var(--space-3)' }}>
            {coding.glAccountCode && <div><span>{t('GL account')}</span><strong>{coding.glAccountCode}</strong></div>}
            {coding.costCenter && <div><span>{t('Cost center')}</span><strong>{coding.costCenter}</strong></div>}
          </section>
        )}

        {po.notes && (
          <section className="rq-doc__notes">
            <span className="rq-doc__section-label">{t('Notes')}</span>
            <p>{po.notes}</p>
          </section>
        )}

        {(po.submittedAt || po.decidedByName || decidedSteps.length > 0) && (
          <section className="rq-doc__sign">
            {po.submittedAt && (
              <div className="rq-doc__sign-block">
                <span className="rq-doc__section-label">{t('Raised by')}</span>
                <div className="rq-doc__sign-mark">
                  {po.submittedSignature ? <img src={po.submittedSignature} alt="" /> : null}
                </div>
                <div className="rq-doc__sign-name">{po.createdByName || '—'}</div>
                <div className="rq-doc__sign-meta">{dateTime(po.submittedAt)}</div>
              </div>
            )}
            {hasChain
              ? decidedSteps.map((s) => (
                <div className="rq-doc__sign-block" key={s.order}>
                  <span className="rq-doc__section-label">
                    {s.status === 'Rejected' ? t('Rejected by') : t('Approved by')} · {s.roleName || t('Step {order}', { order: s.order })}
                  </span>
                  <div className="rq-doc__sign-mark">
                    {s.signature ? <img src={s.signature} alt="" /> : null}
                  </div>
                  <div className="rq-doc__sign-name">{s.decidedByName}</div>
                  <div className="rq-doc__sign-meta">{dateTime(s.decidedAt)}</div>
                </div>
              ))
              : po.decidedByName && (
                <div className="rq-doc__sign-block">
                  <span className="rq-doc__section-label">{po.status === 'Rejected' ? t('Rejected by') : t('Approved by')}</span>
                  <div className="rq-doc__sign-mark">
                    {po.decisionSignature ? <img src={po.decisionSignature} alt="" /> : null}
                  </div>
                  <div className="rq-doc__sign-name">{po.decidedByName}</div>
                  <div className="rq-doc__sign-meta">{dateTime(po.decidedAt)}</div>
                </div>
              )}
          </section>
        )}

        {history.length > 0 && (
          <section className="rq-doc__history">
            <span className="rq-doc__section-label">{t('Audit trail')}</span>
            <ul>
              {history.map((a) => (
                <li key={a.id}>
                  <span className="rq-doc__history-date">{dateTime(a.createdAt)}</span>
                  <span className="rq-doc__history-text">
                    {a.type === 'Commented' ? `${a.actorName || t('Someone')}: “${a.summary}”` : `${a.actorName || t('Someone')} ${a.summary}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="rq-doc__footer">
          <span>{po.number} · {property?.name || ''}</span>
          <span>{t('Generated {timestamp}', { timestamp: dateTime(new Date()) })}</span>
        </footer>
      </div>
    </div>
  );
}
